<template>
  <div v-if="facility && show" class="facility-card" @click="tracking()">
    <NuxtLink :to="strippedURL(facility.slug)" class="facility-card-link">
      <StoryblokComponent
        v-if="media.asset.filename"
        :blok="media"
        :size-hint="sizeHint"
      />
      <div class="text size-2xs">
        <p v-if="facility.facilityTypeGroupKey">
          <span
            class="facility-type"
            :class="facility.facilityTypeGroupKey"
            >{{ useMicroCopy(`servicelocator.label.${facility.facilityTypeGroupKey?.toString().replace('-','')}`) }}</span>
        </p>
        <h2 class="size-m">{{ facility.name }}</h2>
        <div class="address">
          <SvgIcon name="func_locator" />
          <address>
            {{ facility.address }} {{ facility.addressSupplement }},
            {{ facility.zipCode }} {{ facility.city }}
          </address>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<script setup>
const trackingStore = useTrackingStore();
const props = defineProps({
  facility: {
    type: Object,
    required: true,
  },
  position: {
    type: Number,
    required: false,
    default: 0,
  },
  filters: {
    type: Array,
    required: false,
    default: () => {
      return [];
    },
  },
  includeFacilityTypeGroupKey: {
    type: Array,
    required: false,
    default: () => {
      return [];
    },
  },
  sizeHint: {
    type: Number,
    required: false,
    default: 100,
  },
});

const tracking = () => {
  if (props.facility.__typename === "AlgoliaSearchResult") {
    trackingStore.sendTrackingData({
      event: "internal_search_result.click",
      click: useSnakeCaseFilter(props?.facility?.name),
      click_chapter1: "search_results",
      click_chapter2: "link",
      ise_keyword: useSnakeCaseFilter(trackingStore.getKeywords),
      ise_click_rank: props.position + 1,
    });
  }
};

const media = computed(() => {
  return createAssetImage(props.facility?.image);
});

const show = computed(() => {
  return props.includeFacilityTypeGroupKey?.length
    ? props.includeFacilityTypeGroupKey.find(
        (item) =>
          item?.toLowerCase() ===
            props.facility?.facilityTypeGroupKey?.toLowerCase()
      )
    : props.filters?.length
    ? props.filters.find(
        (item) =>
          item.isIncluded &&
          item.name?.toLowerCase() ===
            props.facility?.facilityTypeGroupKey?.toLowerCase()
      )
    : true;
});
</script>

<style lang="scss" scoped>
.facility {
  &-card {
    padding: 0.625rem 0.625rem 2rem 0.625rem;
    border-radius: $radius-xs;
    background-color: var(--solid-07);
    overflow: hidden;
    cursor: pointer;

    @include for-tablet-landscape-up {
      padding-bottom: 3rem;
    }

    &-link {
      display: block;
      width: 100%;
      text-decoration: none;
    }
    & > .facility-card-link * {
      pointer-events: none;
    }
  }
  &-type {
    position: relative;
    display: inline-block;
    margin-bottom: 0.75rem;
    border-radius: $radius-l;
    border: solid 1px var(--solid-02);
    padding: 0.125rem 0.5rem 0.125rem 1.75rem;
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0.5rem;
      transform: translate3d(0, -50%, 0);
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background-color: var(--solid-02);
    }
    &.nursing-home {
      border-color: var(--sl-01);
      &::before {
        background-color: var(--sl-01);
      }
    }
    &.daycare {
      border-color: var(--sl-03);
      &::before {
        background-color: var(--sl-03);
      }
    }
    &.senior-residence {
      border-color: var(--sl-02);
      &::before {
        background-color: var(--sl-02);
      }
    }
    &.home-care {
      border-color: var(--sl-03);
      &::before {
        background-color: var(--sl-03);
      }
    }
  }
}
:deep(.asset-image) {
  border-radius: $radius-xs;
  aspect-ratio: 1.8;
  width: 100%;
}
.text {
  padding: 1.5rem 0.62rem 0;

  @include for-tablet-landscape-up {
    padding-top: 2rem;
  }
}
.address {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  margin-top: 0.3rem;
  address {
    font-style: normal;
  }
  .icon {
    color: var(--brand);
  }
}
</style>
